import { useState } from 'react';
import { del, patch, post } from '../lib/api';
import IconPicker from './IconPicker';
import { ErrorNote, Sheet } from './ui';
import { WEEKDAYS } from '../lib/format';
import type { Chore, Frequency } from '../lib/types';

interface Props {
  childId: number;
  chore: Chore | null; // null = new chore
  onClose: () => void;
  onSaved: () => void;
}

export default function ChoreEditor({ childId, chore, onClose, onSaved }: Props): JSX.Element {
  const [title, setTitle] = useState(chore?.title ?? '');
  const [icon, setIcon] = useState(chore?.icon ?? '✅');
  const [notes, setNotes] = useState(chore?.notes ?? '');
  const [frequency, setFrequency] = useState<Frequency>(chore?.frequency ?? 'daily');
  const [weekdays, setWeekdays] = useState<number[]>(
    chore ? chore.weekdays.split(',').map(Number).filter(Boolean) : [1, 2, 3, 4, 5, 6, 7],
  );
  const [dueWeekday, setDueWeekday] = useState(chore?.due_weekday ?? 7);
  const [dueDayOfMonth, setDueDayOfMonth] = useState(chore?.due_day_of_month ?? 28);
  const [anchorDate, setAnchorDate] = useState(chore?.anchor_date ?? new Date().toISOString().slice(0, 10));
  const [points, setPoints] = useState(chore?.points ?? 1);
  const [countsForPay, setCountsForPay] = useState((chore?.counts_for_pay ?? 1) === 1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function toggleWeekday(value: number) {
    setWeekdays((current) =>
      current.includes(value) ? current.filter((d) => d !== value) : [...current, value].sort(),
    );
  }

  async function save() {
    setError(null);
    if (!title.trim()) {
      setError('Give the chore a name.');
      return;
    }
    if (frequency === 'daily' && weekdays.length === 0) {
      setError('Pick at least one day.');
      return;
    }
    setBusy(true);
    const body = {
      child_id: childId,
      title: title.trim(),
      icon,
      notes: notes.trim(),
      frequency,
      weekdays,
      due_weekday: dueWeekday,
      due_day_of_month: dueDayOfMonth,
      anchor_date: anchorDate,
      points,
      counts_for_pay: countsForPay,
    };
    try {
      if (chore) await patch(`/api/chores/${chore.id}`, body);
      else await post('/api/chores', body);
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!chore) return;
    if (!confirm(`Remove "${chore.title}"? Past completions are kept.`)) return;
    setBusy(true);
    try {
      await del(`/api/chores/${chore.id}`);
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={chore ? 'Edit chore' : 'New chore'} onClose={onClose}>
      <ErrorNote error={error} />
      <label className="field">
        <span>Name</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Empty the dishwasher" />
      </label>

      <IconPicker value={icon} onChange={setIcon} />

      <label className="field">
        <span>How often</span>
        <select value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)}>
          <option value="daily">Daily (choose which days)</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every two weeks</option>
          <option value="monthly">Monthly</option>
        </select>
      </label>

      {frequency === 'daily' && (
        <div className="field">
          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
            On these days
          </span>
          <div className="chips">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                className={`chip${weekdays.includes(day.value) ? ' on' : ''}`}
                onClick={() => toggleWeekday(day.value)}
              >
                {day.short}
              </button>
            ))}
          </div>
        </div>
      )}

      {(frequency === 'weekly' || frequency === 'biweekly') && (
        <label className="field">
          <span>Done by</span>
          <select value={dueWeekday} onChange={(e) => setDueWeekday(Number(e.target.value))}>
            {WEEKDAYS.map((day) => (
              <option key={day.value} value={day.value}>
                {day.long}
              </option>
            ))}
          </select>
        </label>
      )}

      {frequency === 'biweekly' && (
        <label className="field">
          <span>Starting from (sets which fortnight)</span>
          <input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} />
        </label>
      )}

      {frequency === 'monthly' && (
        <label className="field">
          <span>Done by day of month</span>
          <input
            type="number"
            min={1}
            max={31}
            value={dueDayOfMonth}
            onChange={(e) => setDueDayOfMonth(Math.min(31, Math.max(1, Number(e.target.value))))}
          />
        </label>
      )}

      <label className="field">
        <span>Worth (points)</span>
        <input
          type="number"
          min={1}
          max={20}
          value={points}
          onChange={(e) => setPoints(Math.min(20, Math.max(1, Number(e.target.value))))}
        />
      </label>

      <label className="field">
        <span>Notes (optional)</span>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Rinse first!" />
      </label>

      <div className="toggle">
        <span className="small">
          Counts towards pocket money
          <br />
          <span className="tiny muted">Turn off for extra/bonus jobs.</span>
        </span>
        <input type="checkbox" checked={countsForPay} onChange={(e) => setCountsForPay(e.target.checked)} />
      </div>

      <div className="row" style={{ marginTop: 16 }}>
        {chore && (
          <button type="button" className="btn danger" onClick={remove} disabled={busy}>
            Remove
          </button>
        )}
        <button type="button" className="btn grow" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save chore'}
        </button>
      </div>
    </Sheet>
  );
}
